import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import moment from "moment";
import { IMqttMessage } from "ngx-mqtt";
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import {
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Control } from "src/model/class/general classes/control";
import { ControlGroup } from "src/model/class/general classes/control-group";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { DuplicateData } from "src/model/class/general classes/Duplicate-data";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iControl } from "src/model/interface/iControl";
import { iControlGroup } from "src/model/interface/iControl-group";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class LoadControlService implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Load-control.service";
  private _loadControlStore: GenericApiData = null;
  private _loadControlSubject: BehaviorSubject<Array<ControlGroup>> =
    new BehaviorSubject<Array<ControlGroup>>(null);

  private _isErrorNotSupported: boolean = false;

  private _isErrorInGetControlGroupListAPIRequest: boolean = false;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  get isErrorInGetControlGroupListAPIRequest(): boolean {
    return this._isErrorInGetControlGroupListAPIRequest;
  }

  get isErrorNotSupported(): boolean {
    return this._isErrorNotSupported;
  }

  public needToInitialize: boolean = true;

  public readonly controlGroupList = this._loadControlSubject.asObservable();

  private mutex = new Mutex();

  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _storageService: LocalStorageService,
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _store: Store
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  ngOnDestroy() {
    this._loadControlStore = null;

    this._loadControlSubject.next(null);
    this._loadControlSubject.complete();

    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.initializeControlGroupList();
      })
    );

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_CONTROLGROUP);

        await this.unsubscribeToMQTTAddresses();

        this._loadControlStore = null;
        this._loadControlSubject.next(null);
        this.needToInitialize = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Initialize public observable list trasmission: it should be call in
   * the constructor of all component/page
   */
  public Init() {
    if (this.needToInitialize) {
      this.initializeControlGroupList();

      this.needToInitialize = false;
    }
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.needToInitialize = true;
    this.Init();
  }

  /**
   * Initialize controlGroup list: first it read from storage (via getFromStorage()
   * function) then if storage read failed it tries to retrieve data from API
   */
  private initializeControlGroupList() {
    this.GetFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
        this.GetControlGroup();
      } else {
        this.GetControlGroupByLastModify(resultReadStorage);
      }
    });
  }

  //#region Mqtt-addresses handling

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_CONTROL_GROUP_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CONTROL_GROUP_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._loadControlStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling
           * event: when this fired multiple times it perform execution without
           * waiting previous execution complete
           */
          if (message.length === 3) {
            const release = await this.mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idControlGroup = parseInt(message[1]);
              const clientID = message[2];
              /** Finding operation kind: Insert, Update, Delete */
              if (operationKind === GenericOperationKind.INSERT) {
                const indexControlGroup = this._loadControlStore.data.findIndex(
                  (controlGroup: ControlGroup) =>
                    controlGroup.id === idControlGroup
                );
                if (indexControlGroup < 0) {
                  const insertControlGroup: ControlGroup =
                    await this.GetControlGroupByID(idControlGroup).catch(
                      (error) => {
                        return null;
                      }
                    );

                  if (insertControlGroup) {
                    insertControlGroup.p1P2Time =
                      insertControlGroup.p1P2Time / 60;
                    this._loadControlStore.data.push(insertControlGroup);
                    this._loadControlStore.data =
                      this._loadControlStore.data.filter(
                        (controlGroup: ControlGroup) => controlGroup.id !== null
                      );
                  } else {
                    throw new Error(
                      "OnInsertControlGroup - Error retrieving controlGroup with id= " +
                      idControlGroup
                    );
                  }
                }
              } else if (operationKind === GenericOperationKind.UPDATE) {
                const indexUpdateControlGroup =
                  this._loadControlStore.data.findIndex(
                    (controlGroup: ControlGroup) =>
                      controlGroup.id === idControlGroup
                  );
                if (indexUpdateControlGroup >= 0) {
                  const updateControlGroup: ControlGroup =
                    await this.GetControlGroupByID(idControlGroup).catch(
                      (error) => {
                        return null;
                      }
                    );
                  if (updateControlGroup) {
                    updateControlGroup.p1P2Time =
                      updateControlGroup.p1P2Time / 60;
                    this._loadControlStore.data[indexUpdateControlGroup] =
                      updateControlGroup;
                    this._loadControlStore.data =
                      this._loadControlStore.data.filter(
                        (controlGroup: ControlGroup) => controlGroup.id !== null
                      );
                  } else {
                    throw new Error(
                      "OnUpdateControlGroup - Error retrieving control group with id= " +
                      idControlGroup
                    );
                  }
                } else {
                  throw new Error(
                    "OnUpdateControlGroup control group not found id= " +
                    idControlGroup
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                this._loadControlStore.data =
                  this._loadControlStore.data.filter(
                    (controlGroup: ControlGroup) =>
                      controlGroup.id !== idControlGroup
                  );
              }
              return idControlGroup + "|" + clientID;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnControlGroupDataChange - Incorrect message format"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChanged) => {
          promiseIDChanged.then((IDChanged_ClientID) => {
            if (IDChanged_ClientID) {
              const values = IDChanged_ClientID.split("|");
              /** Update store with control group list changed and propagate modify */
              this._loadControlStore.lastAPIModified = moment().format(
                MomentFormats.MOMENT_DATETIME_FORMAT
              );
              this._loadControlSubject.next(this._loadControlStore.data);
              this._eventService.OncontrolGroupChange.next(IDChanged_ClientID);
              this.setToStorage(this._loadControlStore);
            }
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_CONTROL_GROUP_CHANGED",
            "Incorrect message format: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion

    //#region Subscribing to RESERVED_DB_CONTROL_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CONTROL_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._loadControlStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          if (message.length === 3 && this._loadControlStore) {
            const id = parseInt(message[0]);
            const idControl = parseInt(message[1]);
            const clientID = message[2];

            return id + "|" + clientID;
          } else {
            throw new Error("OnControlChange - Incorrect format message");
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChanged) => {
          promiseIDChanged.then((IDChanged_ClientID) => {
            this._loadControlStore.lastAPIModified = moment().format(
              MomentFormats.MOMENT_DATETIME_FORMAT
            );
            this._loadControlSubject.next(this._loadControlStore.data);
            this._eventService.OncontrolGroupChange.next(IDChanged_ClientID);
            this.setToStorage(this._loadControlStore);
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_CONTROL_CHANGED",
            "Error: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region ControlGroup API operations
  /**
   *
   * @param idControlGroup id of the control group to be deleted
   * Delete control group item by id.
   * It cast Observable to Promise (with .toPromise()) and the update private
   * list, storage list and public observable list, otherwise return error.
   * As update and insert operation, store updates are made by mqtt event
   * subscription
   */
  async DeleteControlGroup(idControlGroup: number): Promise<any> {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_CONTROL_GROUP +
        "/" +
        idControlGroup
      )
      .pipe(
        tap((cancel) => {
          const currentControlGroup: ControlGroup =
            this._loadControlStore.data.find(
              (controlGroup: ControlGroup) => controlGroup.id === idControlGroup
            );

          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CONTROL_GROUP_CHANGED;

          const mqttMessage =
            GenericOperationKind.DELETE.toString() +
            "|" +
            currentControlGroup.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadControlGroup();
        })
      )
      .toPromise()
      .then(
        (duplicatedControlGroup: ControlGroup) => {
          return duplicatedControlGroup;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   *
   * @param idControlGroup id of the control group to be updated
   * @param controlGroupData control group data to be modified
   *
   * Update control group item by id.
   * As insert and delete operation, store updates are made by mqtt event subscription
   */
  async UpdateControlGroup(
    idControlGroup: number,
    controlGroupData: iControlGroup
  ): Promise<ControlGroup> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "UpdateControlGroup()",
      "controlGroupData: " + JSON.stringify(controlGroupData)
    );

    const indexControlGroup = this._loadControlStore.data.findIndex(
      (controlGroup) => idControlGroup === controlGroup.id
    );
    controlGroupData.p1P2Time = controlGroupData.p1P2Time * 60;
    if (indexControlGroup >= 0) {
      return this._http
        .put(
          environment.httpServerRoot +
          AppSettings.API_URL_CONTROL_GROUP +
          "/" +
          idControlGroup,
          controlGroupData
        )
        .pipe(
          tap((updateConrtolGroup: ControlGroup) => {
            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_CONTROL_GROUP_CHANGED;

            const mqttMessage =
              GenericOperationKind.UPDATE.toString() +
              "|" +
              updateConrtolGroup.id.toString();

            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadControlGroup();
          })
        )
        .toPromise()
        .then(
          (updatecontrolGroup: ControlGroup) => {
            return updatecontrolGroup;
          },
          (error) => {
            throw new Error(error);
          }
        );
    } else {
      return Promise.reject();
    }
  }

  /**
   *
   * @param controlGroupData control group data to be inserted
   *
   * Insert new control group item.
   * As update and delete operation, store updates are made via mqtt subscription
   */
  async InsertControlGroup(
    controlGroupData: iControlGroup
  ): Promise<ControlGroup> {
    controlGroupData.p1P2Time = controlGroupData.p1P2Time * 60;
    return this._http
      .post<ControlGroup>(
        environment.httpServerRoot + AppSettings.API_URL_CONTROL_GROUP + "/",
        controlGroupData
      )
      .pipe(
        tap((insertControlGroup: ControlGroup) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CONTROL_GROUP_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            insertControlGroup.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadControlGroup();
        })
      )
      .toPromise()
      .then(
        (insertcontrolGroup: ControlGroup) => {
          return insertcontrolGroup;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.post -  AppSettings.API_URL_CONTROL_GROUP ",
            "GetControlGroup()",
            "ERRORE " + error.message
          );
          throw new Error(error);
        }
      );
  }

  /**
   *
   * Get control group data from API. If api call fallback in error constructioln,
   * it set the appriopriate flage "_isErrorInGetControlGroupListAPIRequest" to TRUE,
   * it set the store to NULL and emit on the observable a neutral value (IE. empty array).
   *
   */
  private GetControlGroup(): Subscription {
    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_CONTROL_GROUP
      )
      .subscribe(
        (returnControlGroupSore: GenericApiData) => {
          this._isErrorInGetControlGroupListAPIRequest = false;
          this._loadControlStore = returnControlGroupSore;
          for (let i = 0; i < returnControlGroupSore.data.length; i++) {
            returnControlGroupSore.data[i].p1P2Time =
              returnControlGroupSore.data[i].p1P2Time / 60;
          }

          this._loadControlSubject.next(returnControlGroupSore.data);
          this.setToStorage(returnControlGroupSore);
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.get -  AppSettings.API_URL_CONTROL_GROUP ",
            "GetControlGroup()",
            "Errore.status " + error.status
          );
          if (error.status === ApiErrorType.NOT_FOUND) {
            this._loadControlSubject.error(error);
          } else {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              "_http.get -  AppSettings.API_URL_CONTROL_GROUP ",
              "GetControlGroup()",
              "ERRORE " + error.message
            );
            this._isErrorInGetControlGroupListAPIRequest = true;
            this._loadControlStore = null;
            this._loadControlSubject.next([]);
          }
        }
      );
  }

  /**
   * Get control groups data from API with last modify data store.
   * If api call fallback in error contructor, it propagate an observable
   * notification with .error() function
   * If original data store isn't changed, api return nothing (code 200)
   */
  private GetControlGroupByLastModify(controlGroupStore: GenericApiData) {
    const queryString: string =
      "?lastModified=" + encodeURIComponent(controlGroupStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_CONTROL_GROUP +
        queryString
      )
      .subscribe(
        (returncontrolGroupStore: GenericApiData) => {
          this._isErrorInGetControlGroupListAPIRequest = false;

          if (returncontrolGroupStore) {
            controlGroupStore = returncontrolGroupStore;
            for (let i = 0; i < controlGroupStore.data.length; i++) {
              controlGroupStore.data[i].p1P2Time =
                controlGroupStore.data[i].p1P2Time / 60;
            }
            this.setToStorage(controlGroupStore);
          }

          this._loadControlStore = controlGroupStore;
          this._loadControlSubject.next(controlGroupStore.data);
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.get -  controlGroupStore.lastModified ",
            "GetControlGroupByLastModify()",
            "ERROR status" + error.status
          );
          if (error.status === ApiErrorType.NOT_FOUND) {
            this._loadControlSubject.error(error);
          } else {
            this._isErrorInGetControlGroupListAPIRequest = true;
            this._loadControlSubject.next([]);
          }
        }
      );
  }

  /**
   * Get control groups data from API.
   */
  private async GetControlGroupByID(idControlGroup: number) {
    const queryString: string = "?id=" + idControlGroup;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_CONTROL_GROUP +
        queryString
      )
      .pipe(
        switchMap((returnControlGroupList: GenericApiData) => {
          /**
           * Clean API object from unnecessary data and check if data key
           * is populated: if length is equal to zero, no control group
           * is finded
           */
          if (returnControlGroupList.data.length > 0) {
            const returnControlGroup: ControlGroup =
              returnControlGroupList.data[0];

            return of(returnControlGroup);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returncontrolGroup: ControlGroup) => {
          return returncontrolGroup;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  //#region Duplicate
  /**
   *
   * @param duplicateData Data required for duplication
   *
   * Duplicate a load control and return a list of the load controls created.
   * It cast Observable to Promise (with .toPromise())
   * and then update private list, storage list and public observable list,
   * otherwise return error. As update and insert operatio, store updates are made
   * by mqtt event subscription
   */
  public async DuplicateControlGroup(
    duplicateData: DuplicateData
  ): Promise<ControlGroup[]> {
    return this._http
      .post<ControlGroup[]>(
        environment.httpServerRoot +
        AppSettings.API_URL_DUPLICATE_CONTROL_GROUP +
        "/",
        duplicateData
      )
      .pipe(
        tap((listControlGroup: ControlGroup[]) => {
          for (let i = 0; i < listControlGroup.length; i++) {
            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_CONTROL_GROUP_CHANGED;

            const mqttMessage =
              GenericOperationKind.INSERT.toString() +
              "|" +
              listControlGroup[i].id.toString();

            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadControlGroup();
          }
        })
      )
      .toPromise()
      .then(
        (duplicateCtrlGroups: ControlGroup[]) => {
          return duplicateCtrlGroups;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   * 
   * @param duplicateData  Data required for duplication
   * 
   * Duplicate a control and return a list of the controls created.
   * It cast Observable to Promise (with .toPromise())
   * and then update private list, storage list and public observable list, 
   * otherwise return error. As update and insert operatio, store updates are made 
   * by mqtt event subscription
   */
  public async DuplicateControl(duplicateData: DuplicateData): Promise<Control[]> {
    return this._http.post<Control[]>(
      environment.httpServerRoot +
      AppSettings.API_URL_DUPLICATE_CONTROL + "/",
      duplicateData
    )
      .pipe(
        tap((listControl: Control[]) => {
          for (let i = 0; i < listControl.length; i++) {
            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_CONTROL_CHANGED;

            const mqttMessage =
              GenericOperationKind.INSERT.toString() +
              "|" +
              listControl[i].id.toString();

            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadControlGroup();
          }
        })
      )
      .toPromise()
      .then(
        (duplicateControlList: Control[]) => {
          return duplicateControlList;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }
  //#endregion

  //#region Control API operations
  /**
   *
   * @param idControl ID of the control to delete
   * 
   * Delete control item by id.
   */
  public async DeleteControl(idControl: number): Promise<any> {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_CONTROL +
        "/" +
        idControl
      )
      .pipe(
        tap((cancel) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CONTROL_CHANGED;

          const mqttMessage =
            GenericOperationKind.DELETE.toString() + "|" + idControl.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          const mqttLogicAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_BIGLOGICS_REFRESH;
          this.publishAddress(mqttLogicAddress, "");

          this.SendReservedReloadControlGroup();
        })
      )
      .toPromise();
  }

  /**
   *
   * @param idControl ID of the control to modify
   * @param controlData control data to be modified
   *
   * Update control item by id
   * As insert and delete operation, store updates are made by mqtt event subscription
   */
  public async UpdateControl(
    idControl: number,
    controlData: iControl
  ): Promise<Control> {
    return this._http
      .put(
        environment.httpServerRoot +
        AppSettings.API_URL_CONTROL +
        "/" +
        idControl,
        controlData
      )
      .pipe(
        tap((updateControl: Control) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CONTROL_CHANGED;

          const mqttMessage =
            GenericOperationKind.UPDATE.toString() +
            "|" +
            updateControl.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          //mqttLogicAddresss?

          this.SendReservedReloadControlGroup();
        })
      )
      .toPromise()
      .then(
        (updateControl: Control) => {
          return updateControl;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   * 
   * @param controlData control data to be inserted
   *
   * Insert new control item.
   * As update and delete operation, store updates are made via mqtt subscription
   */
  public async InsertControl(controlData: iControl): Promise<Control> {
    return this._http
      .post<Control>(
        environment.httpServerRoot + AppSettings.API_URL_CONTROL + "/",
        controlData
      )
      .pipe(
        tap((insertControl: Control) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CONTROL_CHANGED;

          const mqttMessage =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "|" +
            insertControl.id.toString();

          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadControlGroup();
        })
      )
      .toPromise()
      .then(
        (insertcontrol: Control) => {
          return insertcontrol;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   *
   * @param idControlGroup control group id
   *
   * Get controls data from API.
   */
  public async GetControlByControlGroupID(idControlGroup: number) {
    let params = new HttpParams();
    params = params.append("idControlGroup", idControlGroup.toString());
    const returnControls = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_CONTROL,
        { params: params }
      )
      .pipe(
        switchMap((returnControlList: GenericApiData) => {
          if (returnControlList.data.length > 0) {
            const controls: Array<Control> = new Array<Control>();
            for (let i = 0; i < returnControlList.data.length; i++) {
              controls.push(returnControlList.data[i]);
            }

            return of(controls);
          } else {
            return new Array<Control>();
          }
        })
      )
      .toPromise();
    return returnControls;
  }
  //#endregion

  //#region Storage
  protected GetFromStorage(): Promise<object> {
    //get from storage and insert in local list of controlGroups
    return this._storageService.get(AppSettings.KEY_CONTROLGROUP).then(
      (controlGroupStorage: GenericApiData) => {
        if (controlGroupStorage) {
          return controlGroupStorage;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setToStorage(controlGroupStore: GenericApiData) {
    this._storageService.set(AppSettings.KEY_CONTROLGROUP, controlGroupStore);
  }
  //#endregion

  //#region mqtt
  publishAddress(mqttAddress: string, value: string) {
    this._mqttService.publishAddress(mqttAddress, value);
  }
  //#endregion

  private SendReservedReloadControlGroup() {
    const mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_RELOAD_LOAD_CONTROL;

    this._mqttService.publishAddress(mqttAddress, "1", "REFRESH");
  }
}
